/* global fetchUrl */
import React, { useEffect, useState, Fragment } from 'react';
import ReactDOM from 'react-dom';

import App from 'App';
import moment from 'moment';

import { formatFigure } from 'Helpers/formatService';

import Button from 'CommonComponents/Button';

import ArrowDownIcon from 'Assets/icons/arrow-down.svg';
import ArrowUpIcon from 'Assets/icons/arrow-up.svg';
import ChevronDownIcon from 'Assets/icons/chevron-down.svg';
import ChevronUpIcon from 'Assets/icons/chevron-up.svg';
import CircleCheckIcon from 'Assets/icons/circle-check.svg';
import CircleXMarkIcon from 'Assets/icons/circle-xmark.svg';
import HourglassIcon from 'Assets/icons/hourglass.svg';
import MinusIcon from 'Assets/icons/minus.svg';
import SearchIcon from 'Assets/icons/search.svg';

const INDECATOR_GROUPS = [
    [
        { icon: 'scope_issue_to_market', label: $t('Scope Issue to Market') },
        { icon: 'response_due_date', label: $t('Response Due Date') },
        { icon: 'decision_date', label: $t('Decision Date') },
        { icon: 'contract_awarded_date', label: $t('Contract Awarded Date') },
        { icon: 'contract_completion_date', label: $t('Contract Completion Date') },
    ],
];

const INITIAL_LOADING_STATE = { procurements: true };

export default function ProcurementPlanChart({ fetchUrl, chartDataUrl: directChartDataUrl, departmentContextId }) {
    const [plan, setPlan] = useState(null);
    const [isLoading, setIsLoading] = useState(INITIAL_LOADING_STATE);
    const [isFetched, setIsFetched] = useState({});
    const [allocatedItems, setAllocatedItems] = useState({});
    const [unallocatedItems, setUnallocatedItems] = useState([]);
    const [dates, setDates] = useState([]);

    useEffect(() => {
        if (directChartDataUrl) {
            fetchChartData(directChartDataUrl);
        }
        if (fetchUrl) {
            fetchData();
        }
    }, []);

    useEffect(() => {
        if (!directChartDataUrl) {
            fetchChartData(plan?.chartDataUrl);
        }
    }, [plan]);

    const loading = (key, value = true) => {
        setIsLoading((pervLoading) => ({ ...pervLoading, [key]: value }));
    };

    const fetched = (key, value = true) => {
        setIsFetched((pervLoading) => ({ ...pervLoading, [key]: value }));
    };

    const fetchData = () => {
        if (isLoading.data) {
            return;
        }

        loading('data');
        fetched('data', false);

        axios
            .untranslated()
            .get(fetchUrl)
            .then(({ data }) => {
                setPlan(data.plan);
                fetched('data');
            })
            .catch((e) => {
                console.log(e);
                fetched('data', false);
            })
            .finally(() => {
                loading('data', false);
            });
    };

    const fetchChartData = (url) => {
        if (url) {
            axios
                .get(url)
                .then(({ data }) => {
                    // Guard against an unexpected/empty response (e.g. a
                    // redirect-to-HTML from a guard middleware) so the chart
                    // degrades to empty instead of hard-crashing on a missing
                    // `items`/`dates`.
                    const { unallocated = [], ...allocated } = data?.items || {};

                    setAllocatedItems(allocated);
                    setUnallocatedItems(unallocated);
                    setDates(data?.dates || []);
                })
                .catch((e) => {
                    console.log(e);
                });
        }
    };

    const download = (type) => {
        const url = type === 'pdf' ? plan.downloadPdfUrl : plan.downloadExcelUrl;
        const fileName = type === 'pdf' ? 'procurement_plan.pdf' : 'procurement_plan.xlsx';

        axios.post(url, {}, { responseType: 'blob' }).then((response) => {
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
        });
    };

    return (
        <div className="table__card">
            {plan && !departmentContextId && (
                <div className="tw-flex tw-w-full tw-justify-end tw-mb-6 tw-gap-x-2">
                    <Button variant="primary" onClick={(e) => download('pdf')}>
                        {$t('Download pdf')}
                    </Button>
                    <Button variant="primary" onClick={(e) => download('excel')}>
                        {$t('Download excel')}
                    </Button>
                </div>
            )}
            <div className="tw-flex tw-w-full tw-justify-end tw-mb-6 tw-gap-x-2">
                {INDECATOR_GROUPS.map((indecators, index) => (
                    <div key={index} className="tw-flex tw-space-x-2 max-lg:tw-space-x-0 max-lg:tw-flex-col">
                        {indecators.map(({ icon, label }) => (
                            <div key={label} className="tw-flex tw-items-center tw-gap-x-2 tw-text-nowrap">
                                <div className="tw-basis-6 tw-text-center">
                                    <IndecatorIcon name={icon} />
                                </div>
                                <div className="tw-basis-full tw-text-charcoal-75 tw-text-sm">{label}</div>
                            </div>
                        ))}
                    </div>
                ))}
            </div>
            <div className="inbud-table_wrapper">
                <table className="inbud-table tw-table-fixed">
                    <thead>
                        <tr>
                            <th className="tw-w-44">{$t('Procurement Name')}</th>
                            <th className="tw-w-48">{$t('Description')}</th>
                            <th className="tw-w-28">{$t('Value')}</th>
                            <th className="tw-w-28">{$t('Duration')}</th>
                            <th className="tw-w-28">{$t('Priority')}</th>
                            <th className="tw-w-28">{$t('Status')}</th>
                            {dates.map((date) => {
                                const month = moment(date, 'YYYY-MM-DD');

                                return (
                                    <th className="tw-w-20" key={date}>
                                        <a>{month.format('MM/')}</a>
                                        <br />
                                        <a>{month.format('YYYY')}</a>
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        {!!unallocatedItems?.length && <ProcurementPlanChartGroupRow items={unallocatedItems} heading={$t('Unallocated')} dates={dates} />}
                        {Object.entries(allocatedItems)?.map(([heading, itemes]) => (
                            <ProcurementPlanChartGroupRow key={heading} items={itemes} heading={heading} dates={dates} />
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function ProcurementPlanChartGroupRow({ items, heading, completed, dates }) {
    const [show, setShow] = useState(true);

    return (
        <Fragment>
            <tr>
                <td colSpan={6 + (dates.length || 100)} className="tw-cursor-pointer" onClick={() => setShow(!show)}>
                    <div className="tw-flex tw-items-center tw-gap-x-1.5">
                        <div className="tw-font-semibold">{heading}</div>
                        {show ? <ChevronUpIcon className="tw-w-1.5 tw-h-1.5" /> : <ChevronDownIcon className="tw-w-1.5 tw-h-1.5" />}
                    </div>
                </td>
            </tr>
            {show &&
                items?.map((item) => (
                    <tr key={item.id} className="!tw-bg-white">
                        <td>{item.name}</td>
                        <td>{item.description}</td>
                        <td className="inbud-numeric-cell">{formatFigure(item.value)}</td>
                        {item.duration ? <td className="inbud-numeric-cell">{item.duration}</td> : <td>{$t('No Data')}</td>}
                        <td>
                            {item.priority == 0 && $t('High')}
                            {item.priority == 1 && $t('Medium')}
                            {item.priority == 2 && $t('Low')}
                        </td>
                        <td>
                            {[1].includes(item.procurement_status_id) && $t('Not Started')}
                            {[4].includes(item.procurement_status_id) && $t('In Progress')}
                            {[2, 3, 6].includes(item.procurement_status_id) && $t('Evaluating')}
                            {[5, 7].includes(item.procurement_status_id) && $t('Awarded')}
                        </td>
                        {dates.map((date) => {
                            const month = moment(date, 'YYYY-MM-DD');
                            const start = moment(item.start_month, 'YYYY-MM-DD');
                            const end = moment(item.end_month, 'YYYY-MM-DD');

                            const target_scope_issue_to_market_date = moment(item.target_scope_issue_to_market_date, 'YYYY-MM-DD');
                            const target_response_due_date = moment(item.target_response_due_date, 'YYYY-MM-DD');
                            const target_decision_date = moment(item.target_decision_date, 'YYYY-MM-DD');
                            const target_contract_award_date = moment(item.target_contract_award_date, 'YYYY-MM-DD');
                            const target_contract_completion_date = moment(item.target_contract_completion_date, 'YYYY-MM-DD');

                            const startCompletionPeriod = moment(item.end_month, 'YYYY-MM-DD').add(1, 'M');
                            const completion = item.completion_month ? moment(item.completion_month, 'YYYY-MM-DD') : null;

                            let inRange = date !== item.start_month && month.unix() > start.unix() && month.unix() <= end.unix();
                            let betweenAwardAndCompletion = !!completion && month.unix() >= startCompletionPeriod.unix() && month.unix() <= completion.unix();

                            return (
                                <td key={item.id + '_' + date}>
                                    <div className="tw-space-x-2">
                                        {moment(date).format('YYYY-MM') === target_scope_issue_to_market_date.format('YYYY-MM') && (
                                            <IndecatorIcon name="scope_issue_to_market" />
                                        )}
                                        {moment(date).format('YYYY-MM') === target_response_due_date.format('YYYY-MM') && (
                                            <IndecatorIcon name="response_due_date" />
                                        )}
                                        {moment(date).format('YYYY-MM') === target_decision_date.format('YYYY-MM') && <IndecatorIcon name="decision_date" />}
                                        {moment(date).format('YYYY-MM') === target_contract_award_date.format('YYYY-MM') && (
                                            <IndecatorIcon name="contract_awarded_date" />
                                        )}
                                        {moment(date).format('YYYY-MM') === target_contract_completion_date.format('YYYY-MM') && (
                                            <IndecatorIcon name="contract_completion_date" />
                                        )}
                                    </div>
                                </td>
                            );
                        })}
                    </tr>
                ))}
        </Fragment>
    );
}

function IndecatorIcon({ className = '', name }) {
    switch (name) {
        case 'scope_issue_to_market':
            return <i className={`fa fa-sm fa-s ${className}`} />;
        case 'response_due_date':
            return <i className={`fa fa-sm fa-r ${className}`} />;
        case 'decision_date':
            return <i className={`fa fa-sm fa-d ${className}`} />;
        case 'contract_awarded_date':
            return <i className={`fa fa-sm fa-c ${className}`} />;
        case 'contract_completion_date':
            return (
                <span className={className}>
                    <i className="fa fa-sm fa-c" />
                    <i className="fa fa-sm fa-c" />
                </span>
            );
        case 'high':
            return <ArrowUpIcon className={`tw-w-4 tw-h-4 tw-text-red ${className}`} />;
        case 'medium':
            return <MinusIcon className={`tw-w-4 tw-h-4 tw-text-yellow-400 ${className}`} />;
        case 'low':
            return <ArrowDownIcon className={`tw-w-4 tw-h-4 tw-text-green ${className}`} />;
        case 'not_started':
            return <CircleXMarkIcon className={`tw-w-4 tw-h-4 tw-text-red ${className}`} />;
        case 'in_progress':
            return <HourglassIcon className={`tw-w-4 tw-h-4 tw-text-green ${className}`} />;
        case 'evaluating':
            return <SearchIcon className={`tw-w-4 tw-h-4 tw-text-charcoal ${className}`} />;
        case 'awarded':
            return <CircleCheckIcon className={`tw-w-4 tw-h-4 tw-text-green ${className}`} />;
        default:
            return null;
    }
}

if (document.getElementById('procurementGanttChart')) {
    var root = document.getElementById('procurementGanttChart');
    ReactDOM.render(
        <App>
            <ProcurementPlanChart fetchUrl={fetchUrl} />
        </App>,
        root,
    );
}
